import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { Truck } from "lucide-react";

const SAVED_COUNTRY_KEY = "aam_ship_country";

/**
 * Small destination → shipping price calculator on the artwork detail page.
 *
 * Notifies the parent (via `onChange({ country, price, zone })`) so the buy
 * flow can pass the resolved country to /checkout/session and surface the
 * combined total. Remembers the buyer's last picked country in localStorage.
 */
export const ShippingCalculator = ({ artworkId, onChange }) => {
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState(
    typeof window !== "undefined" ? localStorage.getItem(SAVED_COUNTRY_KEY) || "" : "",
  );
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get("/shipping/zones")
      .then(({ data }) => setCountries(data.countries || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!country || !artworkId) {
      setQuote(null);
      onChange?.(null);
      return;
    }
    setLoading(true);
    api
      .get(`/artworks/${artworkId}/shipping`, { params: { country } })
      .then(({ data }) => {
        setQuote(data);
        onChange?.({ country, price: data.price, zone: data.zone, hasPriceSet: data.has_price_set });
        try {
          localStorage.setItem(SAVED_COUNTRY_KEY, country);
        } catch (storageErr) {
          // Safari private mode and quota-exceeded errors are expected here —
          // we log at debug so the silent failure is at least traceable in dev.
          logger.debug("ShippingCalculator: localStorage write skipped", storageErr);
        }
      })
      .catch((err) => {
        // Falling back to a quoteless view is fine — the artwork page still works.
        setQuote(null);
        onChange?.(null);
        logger.warn(
          "ShippingCalculator quote failed",
          formatApiErrorDetail(err.response?.data?.detail) || err.message,
        );
      })
      .finally(() => setLoading(false));
  }, [country, artworkId, onChange]);

  return (
    <div data-testid="shipping-calculator" className="mt-6 border border-[#E5E0D8] bg-[#F4EFE6]/40 p-4">
      <div className="flex items-center gap-2">
        <Truck className="w-4 h-4 text-[#1E3A8A]" strokeWidth={1.5} />
        <p className="label-eyebrow text-[#1E3A8A]">— Shipping calculator</p>
      </div>
      <div className="mt-3 flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label htmlFor="ship-country" className="text-xs text-[#555]">
            Where are you?
          </label>
          <select
            id="ship-country"
            data-testid="ship-country-select"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="mt-1 w-full rounded-none border-b-2 border-[#E5E0D8] bg-transparent px-0 py-2 focus:outline-none focus:border-[#1E3A8A] text-sm"
          >
            <option value="">Pick a country…</option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div className="text-right sm:w-32 flex-shrink-0">
          <p className="label-eyebrow text-[#555]">Shipping</p>
          <p data-testid="ship-quote" className="font-mono-aam text-xl text-[#111]">
            {loading ? "…" : quote ? quote.price > 0 ? `$${quote.price.toFixed(0)}` : "Free" : "—"}
          </p>
          {quote && (
            <p className="text-[10px] text-[#555] mt-1">{quote.zone_label}</p>
          )}
        </div>
      </div>
      {quote && !quote.has_price_set && quote.price === 0 && (
        <p data-testid="ship-no-price-warning" className="text-xs text-[#A87900] mt-2">
          The artist hasn&apos;t set a shipping price for this region — confirm with the studio before purchase.
        </p>
      )}
    </div>
  );
};
